import {
  KeyboardArrowDownOutlined,
  LanguageOutlined,
} from "@mui/icons-material";
import {
  AppBar as AppBarMui,
  Box,
  MenuItem,
  Select,
  Toolbar,
} from "@mui/material";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { useDimensions, useTheme } from "../../../hooks";
import { ThemeProps } from "../../../models/theme";
import { Logo } from "../../../shared";
import { useQueryGroupTheme, useQueryLogo } from "../../../api/queries";
import { getCorporateType, getStorageToken } from "../../../utils";
import { getContrastTextColor } from "../../../utils/colorManager";

interface HeaderProps {
  theme: ThemeProps;
}

const AppBar = ({ theme }: HeaderProps) => {
  const { isSmall } = useDimensions();
  const availableLanguages = [
    { value: "fr", label: "Français" },
    { value: "en", label: "English" },
  ];
  const [language, setLanguage] = useState<string | null>(null);
  const token = getStorageToken();
  const groupType = getCorporateType(theme?.url_type);

  const { data: dataCorporate } = useQueryGroupTheme({
    id: theme?.id,
    groupType: getCorporateType(theme?.url_type),
    enable: !!theme?.id,
    authorisation: token || "",
  });

  const corporateId = dataCorporate?.id || "";

  const {
    data,
    isLoading: isLoadingLogo,
    isError,
    error,
  } = useQueryLogo({
    groupType,
    corporateId,
    enabled: !!token && !!corporateId,
    authorisation: token || "",
  });

  useEffect(() => {
    setLanguage(i18n.language);

    const handleLanguageChanged = (newLang: string) => {
      if (newLang && (newLang === "en" || newLang === "fr")) {
        setLanguage(newLang);
      }
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  const handleLanguageChange = (event: any) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
  };

  return (
    <AppBarMui position="static" sx={{ backgroundColor: theme.color_primary }}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {data && <Logo image={data && URL.createObjectURL(data as any)} />}
        <Box sx={{ display: "flex" }}>
          {!isSmall && language && (
            <Select
              value={language}
              onChange={handleLanguageChange}
              variant="outlined"
              sx={{
                color: getContrastTextColor(theme.color_secondary),
                backgroundColor: theme.color_secondary,
                marginRight: 0,
                height: "55px",
                minWidth: "153px",
                borderRadius: "10px",
                px: 1,
                "& .MuiSelect-icon": {
                  color: theme.color_primary,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              startAdornment={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: 1,
                  }}
                >
                  <LanguageOutlined />
                </Box>
              }
              IconComponent={() => (
                <KeyboardArrowDownOutlined
                  sx={{ color: getContrastTextColor(theme.color_secondary) }}
                />
              )}
            >
              {availableLanguages.map(lang => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Toolbar>
    </AppBarMui>
  );
};

export default AppBar;
